/**
 * Cloud implementation of the shared WatchlistApi. Node-scoped, like every other analytics
 * adapter: Cloud FE → Cloud BE → tunnel → the node's command-center.
 *
 * The PEOPLE half is the persons registry with `is_blacklisted` applied — flagging is columns
 * on `persons`, not a list beside it — and the SIGHTINGS half is the node's union endpoint.
 */
import type {
  WatchlistApi,
  WatchlistDetectionPage,
  WatchlistPeoplePage,
  WatchlistPerson,
} from '@trinetra/shared';
import {
  blacklistNodePerson,
  getNodePersonsPage,
  getNodeWatchlistDetections,
  getNodeWatchlistSummary,
  unblacklistNodePerson,
} from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

const nid = () => nodeId() || '';

function rethrow(e: any): never {
  const body = e?.response?.data;
  throw new Error(body?.message || body?.error || e?.message || 'Request failed');
}

/** getNodePersonsPage's { rows, total } → the contract's page shape. */
const page = (res: { rows: any[]; total: number }, requested: { page?: number; limit?: number }) => ({
  data: res.rows as WatchlistPerson[],
  pagination: {
    page: requested.page ?? 1,
    limit: requested.limit ?? res.rows.length,
    total: res.total,
    total_pages: Math.max(1, Math.ceil(res.total / (requested.limit || res.rows.length || 1))),
  },
});

export const cloudWatchlistApi: WatchlistApi = {
  people: async (params = {}) => {
    const id = nid();
    // An empty list would read as "nobody is watched for" on a page with no site chosen.
    if (!id) return { data: [], pagination: undefined } as WatchlistPeoplePage;
    const res = await getNodePersonsPage(id, { ...params, is_blacklisted: true }).catch(rethrow);
    return page(res, params) as WatchlistPeoplePage;
  },

  // NOT filtered by is_blacklisted: this is the "who could we add" lookup and has to show
  // people who are not on the list yet.
  searchRegistry: async (params = {}) => {
    const id = nid();
    if (!id) return { data: [], pagination: undefined } as WatchlistPeoplePage;
    const res = await getNodePersonsPage(id, params).catch(rethrow);
    return page(res, params) as WatchlistPeoplePage;
  },

  flagPerson: async (personId, input) => {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    return (await blacklistNodePerson(id, personId, input as object).catch(rethrow)) as WatchlistPerson;
  },

  unflagPerson: async (personId) => {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    await unblacklistNodePerson(id, personId).catch(rethrow);
  },

  detections: async (params = {}) => {
    const id = nid();
    if (!id) return { data: [], pagination: undefined } as WatchlistDetectionPage;
    return (await getNodeWatchlistDetections(id, params as Record<string, any>)
      .catch(rethrow)) as WatchlistDetectionPage;
  },

  detectionSummary: async (params = {}) => {
    const id = nid();
    if (!id) return { person: 0, vehicle: 0, total: 0 };
    return await getNodeWatchlistSummary(id, params as Record<string, any>).catch(rethrow);
  },
};
